import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, Tag, ArrowRight } from 'lucide-react';
import { latestNews as fallbackNews } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsSection = () => {
  const [news, setNews] = useState(fallbackNews);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/news?limit=6`);
        if (active && Array.isArray(res.data) && res.data.length > 0) {
          setNews(res.data);
        }
      } catch (err) {
        // Fall back silently to mock data
        console.warn('News API unavailable, using fallback:', err?.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  return (
    <section id="news" className="relative bg-gray-50 py-32" data-testid="news-section">
      <div className="max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block bg-[#0099FF]/10 border border-[#0099FF]/30 px-4 py-2 rounded-none mb-6">
            <span className="text-[#0099FF] text-sm font-medium">STAY INFORMED</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 mb-6 tracking-tight">
            News &amp; Updates
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay ahead with the latest tax regulations, compliance updates, and business advisory insights.
          </p>
        </div>

        {/* News Grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-500 ${
            loading ? 'opacity-60' : 'opacity-100'
          }`}
          data-testid="news-grid"
        >
          {news.slice(0, 6).map((item) => (
            <div
              key={item.id}
              className="group bg-white border border-gray-200 hover:border-[#0099FF]/50 hover:shadow-xl transition-all duration-400 overflow-hidden"
              data-testid={`news-card-${item.id}`}
            >
              {/* Category Badge */}
              <div className="bg-[#0099FF]/10 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Tag className="text-[#0099FF]" size={16} />
                  <span className="text-[#0099FF] text-xs font-medium uppercase tracking-wide">
                    {item.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Date */}
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="text-gray-400" size={14} />
                  <span className="text-gray-500 text-xs">{item.date}</span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-tight group-hover:text-[#0099FF] transition-colors duration-300">
                  {item.title}
                </h3>

                {/* Excerpt */}
                <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3">
                  {item.excerpt}
                </p>

                {/* Read More Link */}
                <a
                  href={item.link || '#'}
                  className="flex items-center gap-2 text-[#0099FF] text-sm font-medium group-hover:gap-3 transition-all duration-300"
                >
                  <span>Read More</span>
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center mt-12">
          <button
            className="inline-flex items-center gap-2 text-gray-900 hover:text-[#0099FF] font-medium text-lg transition-colors duration-300 group"
            data-testid="news-view-all"
          >
            <span>View All Updates</span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
